/**
 * Renders raffle entry badges in the header cart preview flyout.
 * Called after cart preview HTML is injected (cart-preview.js).
 */
import { loadCacheFromStorage, saveCacheToStorage } from './entries-badge/cache';
import { formatRounded, getStorefrontApiToken, normalizeMultiplier, parseNumber, parsePriceText } from './entries-badge/helpers';
import { fetchRouteMultipliersBatched } from './entries-badge/route-multiplier-fetch';
import { revealBadge, setBadgeContent } from './entries-badge/render';

const BADGE_SELECTOR = '[data-cart-preview-badge="true"]';

function getBadgeMultiplier(badge, multiplierCache) {
    const templateMultiplier = parseNumber(badge.dataset.entriesMultiplier);

    if (!Number.isNaN(templateMultiplier) && templateMultiplier > 0) {
        return normalizeMultiplier(templateMultiplier);
    }

    const productPath = badge.dataset.productPath || '';

    if (productPath && multiplierCache.has(productPath)) {
        return normalizeMultiplier(multiplierCache.get(productPath));
    }

    return NaN;
}

function renderBadge(badge, multiplierCache) {
    const quantity = Math.max(1, Math.round(parseNumber(badge.dataset.quantity)) || 1);
    const unitPrice = parsePriceText(badge.dataset.entriesPriceText || '');
    const multiplier = getBadgeMultiplier(badge, multiplierCache);

    if (Number.isNaN(unitPrice) || unitPrice <= 0 || Number.isNaN(multiplier)) {
        return;
    }

    const perUnitEntries = unitPrice * multiplier;
    const totalEntries = perUnitEntries * quantity;

    setBadgeContent(badge, {
        totalEntries,
        multiplier,
        entriesText: `${formatRounded(totalEntries)} TOTAL ENTRIES`,
        multiplierText: `${formatRounded(perUnitEntries)} x ${quantity}`,
    });

    revealBadge(badge, { pulse: false });
}

/**
 * Process all cart-preview badges inside the given container.
 * Fetches missing multipliers via GraphQL when context.storefrontApiToken is available.
 *
 * @param {Element} containerEl - DOM element containing the cart preview (e.g. #cart-preview-dropdown).
 * @param {Object} context - Theme context; storefrontApiToken used for GraphQL.
 */
export function processCartPreviewBadges(containerEl, context = {}) {
    if (!containerEl || typeof containerEl.querySelectorAll !== 'function') {
        return;
    }

    const badges = Array.from(containerEl.querySelectorAll(BADGE_SELECTOR));

    if (badges.length === 0) {
        return;
    }

    const multiplierCache = loadCacheFromStorage();
    const storefrontApiToken = getStorefrontApiToken(context);

    const pathsToFetch = Array.from(new Set(
        badges
            .map((badge) => ({
                path: (badge.dataset.productPath || '').trim(),
                multiplier: parseNumber(badge.dataset.entriesMultiplier),
            }))
            .filter((entry) => entry.path && !multiplierCache.has(entry.path) && (Number.isNaN(entry.multiplier) || entry.multiplier <= 0))
            .map((entry) => entry.path),
    ));

    function renderAll() {
        badges.forEach((badge) => renderBadge(badge, multiplierCache));
    }

    if (pathsToFetch.length === 0) {
        renderAll();
        return;
    }

    if (!storefrontApiToken) {
        pathsToFetch.forEach((path) => {
            multiplierCache.set(path, normalizeMultiplier(NaN));
        });
        saveCacheToStorage(multiplierCache);
        renderAll();
        return;
    }

    fetchRouteMultipliersBatched(pathsToFetch, storefrontApiToken)
        .then((multipliersByPath) => {
            pathsToFetch.forEach((path) => {
                multiplierCache.set(path, multipliersByPath[path] ?? normalizeMultiplier(NaN));
            });
            saveCacheToStorage(multiplierCache);
            renderAll();
        })
        .catch((err) => {
            console.warn('[Raffle] Cart preview multiplier fetch failed:', err);
            pathsToFetch.forEach((path) => {
                multiplierCache.set(path, normalizeMultiplier(NaN));
            });
            saveCacheToStorage(multiplierCache);
            renderAll();
        });
}
